import type Graphic from "@arcgis/core/Graphic";
import type PopupTemplate from "@arcgis/core/PopupTemplate";
import type FieldInfo from "@arcgis/core/popup/FieldInfo";

type FieldInfoProperties = NonNullable<
	ConstructorParameters<typeof FieldInfo>[0]
>;

/**
 * Specifies the fields that are present in the {@link Graphic.attributes} of a
 * bridge or crossing {@link Graphic}.
 * Use the {@link isFieldInfoAttributes} type guard function to check if
 * an object is a {@link FieldInfoAttributes}.
 */
export interface FieldInfoAttributes
	extends Record<string, string | number | boolean | null> {
	/**
	 * alias: Crossing GUID, length: 38
	 */
	Crossing_GUID: string;
	/**
	 * alias: Title, length: 54
	 */
	Title: string;
	/**
	 * alias: State Structure ID, length: 8
	 */
	State_Structure_ID: string;
	/**
	 * alias: Bridge Number, length: 20
	 */
	Bridge_Number: string;
	/**
	 * alias: State Route Identifier, length: 12
	 */
	State_Route_Identifier: string;
	/**
	 * alias: State Route Milepost
	 */
	State_Route_Milepost: number;
	/**
	 * alias: Is Mainline
	 */
	Is_Mainline: 0 | 1;
	/**
	 * alias: Route Location, length: 56
	 */
	Route_Location: string;
	/**
	 * alias: Minimum Vertical Clearance, length: 4000
	 */
	Minimum_Vertical_Clearance: string;
	/**
	 * alias: Minimum Vertical Clearance Inches
	 */
	Minimum_Vertical_Clearance_Inches: number;
	/**
	 * alias: Maximum Vertical Clearance Inches
	 */
	Maximum_Vertical_Clearance_Inches: number;
	/**
	 * alias: Maximum Vertical Clearance, length: 4000
	 */
	Maximum_Vertical_Clearance: string;
	/**
	 * alias: Increase Advisory, length: 8000
	 */
	Increase_Advisory: string | null;
	/**
	 * alias: Photos, length: 9
	 */
	Photos: "Increase" | "Decrease" | "Both" | "None";
	/**
	 * URL to the increase photo
	 * alias: Increase Photo, length: 8000
	 */
	Increase_Photo: string | null;
	/**
	 * Comma-delimited list of increase lane clearances
	 * E.g., `16′9″,12′0″`
	 * alias: Increase Lane Clearances, length: 1073741822
	 */
	Increase_Lane_Clearances: string | null;
	/**
	 * alias: Decrease Advisory, length: 8000
	 */
	Decrease_Advisory: string | null;
	/**
	 * alias: Decrease Photo, length: 8000
	 */
	Decrease_Photo: string | null;
	/**
	 * alias: Decrease Lane Clearances, length: 1073741822
	 */
	Decrease_Lane_Clearances: string | null;
	/**
	 * alias: Direction, length: 1
	 */
	Direction: "I" | "D" | "B";
}

export const maxVCFieldName = "Maximum_Vertical_Clearance_Inches";
export const stateRouteIdFieldName = "State_Route_Identifier";
export const minVCFieldName = "Minimum_Vertical_Clearance_Inches";
export const Bridge_NumberFieldName = "Bridge_Number";
export const titleFieldName = "Title";
export const State_Structure_IDFieldName = "State_Structure_ID";
// Create an array of strings, each of which is a field name.

export const fieldNames = [
	"Crossing_GUID",
	titleFieldName,
	State_Structure_IDFieldName,
	Bridge_NumberFieldName,
	stateRouteIdFieldName,
	"State_Route_Milepost",
	"Is_Mainline",
	"Route_Location",
	"Minimum_Vertical_Clearance",
	minVCFieldName,
	maxVCFieldName,
	"Maximum_Vertical_Clearance",
	"Increase_Advisory",
	"Photos",
	"Increase_Photo",
	"Increase_Lane_Clearances",
	"Decrease_Advisory",
	"Decrease_Photo",
	"Decrease_Lane_Clearances",
	"Direction",
] as const;

/**
 * Checks if the given attributes object has all the keys of
 * {@link FieldInfoAttributes}.
 * @param attributes - The object to check.
 * @returns true if all the keys are present, false otherwise.
 */
export function isFieldInfoAttributes(
	attributes: unknown,
): attributes is FieldInfoAttributes {
	if (!attributes || typeof attributes !== "object") {
		return false;
	}

	return fieldNames.every((field) => field in attributes);
}

/**
 * Fields for the bridge clearances layer.
 */
export interface BridgeClearanceFieldInfo extends FieldInfoProperties {
	fieldName: `${(typeof fieldNames)[number]}`;
}

/**
 * Checks if the given {@link FieldInfoProperties} object is a {@link BridgeClearanceFieldInfo}.
 * A {@link BridgeClearanceFieldInfo} is a {@link FieldInfoProperties} object where the fieldName property
 * is a valid field name for the bridge clearances layer.
 * @param fieldInfo - the object to check.
 * @returns true if fieldInfo is a {@link BridgeClearanceFieldInfo}, false otherwise.
 */
export function isBridgeClearanceFieldInfo<T extends FieldInfoProperties>(
	fieldInfo: T,
): fieldInfo is T {
	return (
		fieldInfo.fieldName != null &&
		(fieldNames as unknown as string[]).includes(fieldInfo.fieldName)
	);
}

/**
 * Iterates over the given iterable of {@link FieldInfo} objects, and sets isEditable to false.
 * Additionally, if the {@link FieldInfo} object is a {@link BridgeClearanceFieldInfo},
 * sets the number of decimal places for the "SRMP" field to 2.
 * @param fieldInfos - the iterable of {@link FieldInfo} objects.
 */
export function setFieldInfoProperties(
	fieldInfos: NonNullable<PopupTemplate["fieldInfos"]>,
) {
	for (const fieldInfo of fieldInfos) {
		fieldInfo.isEditable = false;
		if (!isBridgeClearanceFieldInfo(fieldInfo)) {
			continue;
		}
		if (fieldInfo.fieldName === "SRMP") {
			if (fieldInfo.format) {
				fieldInfo.format.places = 2;
			}
		}
	}
}
